import { COST_NAV_SECTIONS, type CostNavItem, type CostNavSection } from "./cost-library-nav";
import { getNavSectionDisplayLabel, getNavItemDisplayLabel } from "@workspace/api-zod/quote-headings";

export type CostTaskNavInput = { category: string; trade: string; name: string; description?: string | null; keywords?: string | null };
export type CostNavLocation = { section: CostNavSection; item: CostNavItem };

/** Resolve original metadata, never personalised text. Do not attribute an
 * ambiguous keyword-filtered task to an arbitrary first subcategory. */
export function getCostTaskNavLocation(task: CostTaskNavInput): CostNavLocation | null {
  const text = `${task.name} ${task.description ?? ""} ${task.keywords ?? ""}`.toLowerCase();
  const candidates: Array<CostNavLocation & { score: number }> = [];
  for (const section of COST_NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.categoryFilter && item.categoryFilter !== task.category) continue;
      if (item.tradeFilter && item.tradeFilter !== task.trade) continue;
      const words = item.keywordFilter?.toLowerCase().split(/\s+/).filter(Boolean) ?? [];
      const matches = words.filter(word => text.includes(word)).length;
      if (words.length && !matches) continue;
      candidates.push({ section, item, score: (item.tradeFilter ? 10 : 0) + (matches ? 20 + matches : 0) });
    }
  }
  candidates.sort((a, b) => b.score - a.score);
  if (!candidates.length || (candidates[1] && candidates[1].score === candidates[0]!.score)) return null;
  return candidates[0]!;
}

export function getCostTaskDisplayLabels(
  task: CostTaskNavInput,
  names: Record<string, string> | null | undefined,
  fallback: { sectionLabel: string; itemLabel: string },
  selected?: { sectionId: string | null; itemId: string | null },
  sections: readonly CostNavSection[] = COST_NAV_SECTIONS,
): { sectionLabel: string; itemLabel: string } {
  const selectedSection = selected?.sectionId ? sections.find(s => s.id === selected.sectionId) : undefined;
  const selectedItem = selectedSection?.items.find(i => i.id === selected?.itemId);
  const location = selectedSection && selectedItem
    ? { section: selectedSection, item: selectedItem }
    : getCostTaskNavLocation(task);
  return location ? {
    sectionLabel: getNavSectionDisplayLabel(location.section, names),
    itemLabel: getNavItemDisplayLabel(location.section.id, location.item, names),
  } : fallback;
}
